import logger from "../utils/logger";
import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

/**
 * Hook to fetch the current user's unread message count.
 * Used by Sidebar and MobileMenu to show notification badges.
 * Refreshes on mount, polls every 30s, and listens for realtime inserts on messages.
 */
export default function useUnreadMessages() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setUnreadCount(0);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.rpc("get_unread_message_count");

      if (error) {
        logger.error("Error fetching unread count:", error);
        setUnreadCount(0);
      } else {
        setUnreadCount(data || 0);
      }
    } catch (err) {
      logger.error("Error fetching unread count:", err);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();

    // Poll every 30 seconds as a fallback
    const interval = setInterval(fetchUnreadCount, 30000);

    // Listen for new messages in realtime
    // Every consumer needs its own topic. supabase-js hands back the same
    // channel object for a repeated name, and calling .on() on a channel that
    // already subscribed throws. Sidebar and MobileMenu both mount this hook,
    // so a fixed name breaks the second one.
    const channel = supabase
      .channel(`unread-messages-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "messages" },
        () => {
          // Small delay to let triggers update
          setTimeout(fetchUnreadCount, 500);
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [fetchUnreadCount]);

  return { unreadCount, loading, refresh: fetchUnreadCount };
}
